import { TFunction } from "i18next";
import * as Yup from "yup";

import { CounselorWorkTimeFormValues, WorkTimeAllocationRowValues } from "./CounselorWorkTime.types";

export function counselorWorkTimeValidationSchema(t: TFunction) {
  return Yup.object().shape({
    weeklyHours: Yup.number()
      .typeError(t("common.validation.invalidNumber"))
      .min(0, t("common.validation.invalidNumber"))
      .required(
        t("common.validation.fieldRequired", {
          fieldName: t("counselors.workTime.contractualHours.weeklyHours"),
        })
      ),
    allocations: Yup.array().of(
      Yup.object().shape({
        workArea: Yup.string().required(
          t("common.validation.fieldRequired", { fieldName: t("counselors.workTime.allocation.workArea") })
        ),
        team: Yup.string().required(
          t("common.validation.fieldRequired", { fieldName: t("counselors.workTime.allocation.team") })
        ),
        hours: Yup.number()
          .typeError(t("common.validation.invalidNumber"))
          .min(0)
          .required(
            t("common.validation.fieldRequired", {
              fieldName: t("counselors.workTime.allocation.hoursPerWeek"),
            })
          ),
        validFrom: Yup.date()
          .typeError(t("common.validation.invalidDateFormat"))
          .required(t("common.validation.fieldRequired", { fieldName: t("counselors.workTime.allocation.validFrom") }))
          .test(
            "no-overlapping-period-per-area",
            t("counselors.workTime.validation.overlappingPeriod"),
            (_validFrom, ctx) => {
              const row = ctx.parent as WorkTimeAllocationRowValues;
              const root = ctx.from?.[ctx.from.length - 1]?.value as CounselorWorkTimeFormValues | undefined;
              return !overlapsAnotherPeriodInArea(row, root?.allocations ?? []);
            }
          ),
        validTo: Yup.date()
          .typeError(t("common.validation.invalidDateFormat"))
          .nullable()
          .min(Yup.ref("validFrom"), t("counselors.workTime.validation.invalidDateRange"))
          .test("single-open-period-per-area", t("counselors.workTime.validation.multipleOpen"), (validTo, ctx) => {
            if (validTo != null) {
              return true;
            }

            const { workArea } = ctx.parent as WorkTimeAllocationRowValues;
            const root = ctx.from?.[ctx.from.length - 1]?.value as CounselorWorkTimeFormValues | undefined;
            return isOnlyOpenPeriodInArea(workArea, root?.allocations ?? []);
          }),
      })
    ),
  });
}

function isOnlyOpenPeriodInArea(workArea: string, allocations: WorkTimeAllocationRowValues[]): boolean {
  const openCountInArea = allocations.filter(
    (allocation) => allocation.validTo == null && allocation.workArea === workArea
  ).length;

  return openCountInArea <= 1;
}

const OPEN_PERIOD_END = new Date(8640000000000000);

function overlapsAnotherPeriodInArea(
  row: WorkTimeAllocationRowValues,
  allocations: WorkTimeAllocationRowValues[]
): boolean {
  const rowValidFrom = row.validFrom;
  if (rowValidFrom == null) {
    return false;
  }

  return allocations.some(
    (other) =>
      other !== row &&
      other.workArea === row.workArea &&
      other.validFrom != null &&
      periodsOverlap(rowValidFrom, row.validTo, other.validFrom, other.validTo)
  );
}

function periodsOverlap(startA: Date, endA: Date | null, startB: Date, endB: Date | null): boolean {
  const resolvedEndA = endA ?? OPEN_PERIOD_END;
  const resolvedEndB = endB ?? OPEN_PERIOD_END;

  return startA <= resolvedEndB && startB <= resolvedEndA;
}
